import * as React from "react";
import {
  createStyles,
  WithStyles,
  withStyles,
  StyleRules
} from "@material-ui/core/styles";

// ui
import Paper from "@material-ui/core/Paper";
import SectionTitle from "@components/atoms/SectionTitle";
import KnowbeButton from "@components/presentational/atoms/KnowbeButton";
import { SearchUsersForm } from "@components/organisms/mgr/TANKINYUSHO/record/SearchUsersForm";

// store
import { connect } from "react-redux";
import { AppState } from "@stores/type";
import { UsersInFacilityState } from "@stores/domain/mgr/TANKINYUSHO/userInFacility/types";
import { Dispatch } from "redux";
import * as usersSummaryActions from "@stores/pages/record/usersSummary/actions";

// utils
import uniq from "lodash-es/uniq";

const styles = (): StyleRules =>
  createStyles({
    paperContainer: {
      padding: "30px 32px 32px 32px",
      margin: 16
    },
    ul: {
      listStyle: "none",
      padding: 0,
      marginTop: 48,
      marginBottom: 0
    },
    li: {
      display: "flex",
      alignItems: "center",
      padding: "14px 16px",
      "&:first-child": {
        borderTop: "1px solid #e5e5e5"
      },
      "&:nth-child(2n)": {
        backgroundColor: "#f5f5f5"
      },
      "&:last-child": {
        borderBottom: "1px solid #e5e5e5"
      }
    },
    userName: {
      margin: "0 auto 0 0",
      color: "rgba(0, 0, 0, 0.87)"
    },
    recordButton: {
      marginLeft: 8
    }
  });

type StateProps = {
  users: UsersInFacilityState["users"];
  searchedUsers: UsersInFacilityState["users"];
  searchConditionsIds: number[];
};

type DispatchProps = {
  setCurrentUsers: (users: UsersInFacilityState["users"]) => void;
  setSearchConditions: (searchConditionsIds: number[]) => void;
};

export type PatternListType = {
  id: number;
  targetKana: string;
  viewKana: string;
  checked: boolean;
  disabled: boolean;
};

type MergeProps = {
  patternList: PatternListType[];
  currentUsers: UsersInFacilityState["users"];
} & StateProps &
  DispatchProps;

type Props = MergeProps & WithStyles<typeof styles>;

const patternListFormat: PatternListType[] = [
  {
    id: 1,
    viewKana: "あ",
    targetKana: "[ァ-オ]|[ヴ]",
    checked: false,
    disabled: false
  },
  {
    id: 2,
    viewKana: "か",
    targetKana: "[カ-ゴ]|[ヵ-ヶ]",
    checked: false,
    disabled: false
  },
  {
    id: 3,
    viewKana: "さ",
    targetKana: "[サ-ゾ]",
    checked: false,
    disabled: false
  },
  {
    id: 4,
    viewKana: "た",
    targetKana: "[タ-ド]",
    checked: false,
    disabled: false
  },
  {
    id: 5,
    viewKana: "な",
    targetKana: "[ナ-ノ]",
    checked: false,
    disabled: false
  },
  {
    id: 6,
    viewKana: "は",
    targetKana: "[ハ-ポ]",
    checked: false,
    disabled: false
  },
  {
    id: 7,
    viewKana: "ま",
    targetKana: "[マ-モ]",
    checked: false,
    disabled: false
  },
  {
    id: 8,
    viewKana: "や",
    targetKana: "[ャ-ヨ]",
    checked: false,
    disabled: false
  },
  {
    id: 9,
    viewKana: "ら",
    targetKana: "[ラ-ロ]",
    checked: false,
    disabled: false
  },
  {
    id: 10,
    viewKana: "わ",
    targetKana: "[ヮ-ン]|[ヷ-ヺ]",
    checked: false,
    disabled: false
  }
];

const RecordEachUserCore = (props: Props): JSX.Element => {
  const { classes } = props;
  const handleSearchUsers = (list: PatternListType[]) => (
    e: React.MouseEvent<HTMLElement>
  ): void => {
    e.preventDefault();
    const checkedPatterns = list.filter((item) => item.checked);
    const checkedPatternsIds = checkedPatterns.map((item) => item.id);
    props.setSearchConditions(checkedPatternsIds);
    if (checkedPatterns.length === 0) {
      props.setCurrentUsers(props.users);
    } else {
      const patternArr: string[] = [];
      checkedPatterns.forEach((item) => patternArr.push(item.targetKana));
      const checkKanaReg = new RegExp(`^(${patternArr.join("|")}).*$`);
      const newUsers = props.users.filter((item) =>
        checkKanaReg.test(item.kanaName)
      );
      props.setCurrentUsers(newUsers);
    }
  };

  return (
    <Paper elevation={0} className={classes.paperContainer}>
      <SectionTitle label="利用者ごとの記録" isTitleNoMargin />
      {props.currentUsers.length > 0 && (
        <SearchUsersForm
          patternList={props.patternList}
          handleSearchUsers={handleSearchUsers}
        />
      )}
      <ul className={classes.ul}>
        {props.currentUsers.map((user) => (
          <li key={user.uif_id} className={classes.li}>
            <div className={classes.userName}>{user.displayName}</div>
            <KnowbeButton
              kind="outlineWhite"
              className={classes.recordButton}
              href={`#/record/${user.uif_id}/support`}
            >
              支援記録
            </KnowbeButton>
          </li>
        ))}
      </ul>
    </Paper>
  );
};

const mapStateToProps = (state: AppState): StateProps => ({
  users: state.TANKINYUSHO.userInFacility.users,
  searchedUsers: state.pages.recordUsersSummary.users,
  searchConditionsIds: state.pages.recordUsersSummary.searchConditionsIds
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setCurrentUsers: (users: UsersInFacilityState["users"]): void => {
      dispatch(usersSummaryActions.setCurrentUsers(users));
    },
    setSearchConditions: (searchConditionsIds: number[]): void => {
      dispatch(usersSummaryActions.setSearchConditions(searchConditionsIds));
    }
  };
};

const mergeProps = (
  stateProps: StateProps,
  dispatchProps: DispatchProps
): MergeProps => {
  const extractClickablePatternID = (
    users: UsersInFacilityState["users"]
  ): number[] => {
    const arr: number[] = [];
    [...users].forEach((item) => {
      const existingPattern = patternListFormat.find((pattern) => {
        const checkKanaReg = new RegExp(`^${pattern.targetKana}.*$`);
        return checkKanaReg.test(item.kanaName);
      });
      if (existingPattern) {
        arr.push(existingPattern.id);
      }
    });
    return uniq(arr);
  };

  const currentUsers = stateProps.searchedUsers.length
    ? stateProps.searchedUsers
    : stateProps.users;

  const clickablePatternID = extractClickablePatternID(stateProps.users);
  const patternList = patternListFormat.map((item) => {
    let { disabled, checked } = item;
    if (!clickablePatternID.includes(item.id)) {
      disabled = true;
    } else if (stateProps.searchConditionsIds.includes(item.id)) {
      checked = true;
    }
    return {
      ...item,
      disabled,
      checked
    };
  });
  return {
    patternList,
    currentUsers,
    ...dispatchProps,
    ...stateProps
  };
};

export const RecordEachUser = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(withStyles(styles)(RecordEachUserCore));
